<script setup lang="ts">
    import { toDateOnly } from '@shared/utils/formatDateTime';
    import { reactive } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import {
        listInvoices,
        sendInvoice,
        sendInvoices,
        downloadInvoicePdf,
        renderInvoice,
        registerReminder,
        cancelInvoice,
        Invoice,
    } from './Billing.access';

    const firstOfMonth = toDateOnly(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const lastOfMonth = toDateOnly(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));

    const state = reactive<{
        invoices: Invoice[];
        loading: boolean;
        statusFilter: number;
        orderTypeFilter: number;
        paymentTypeFilter: number;
        invoiceNumber: number | null;
        recipientFilter: string;
        from: string;
        to: string;
        dirtySearch: boolean;
        cancelInvoiceId: string | null;
        cancelInvoiceNumber: string;
        cancelReason: string;
        printInvoiceNumber: string;
        printHtml: string;
        printIsReminder: boolean;
        printLoading: boolean;
    }>({
        invoices: [],
        loading: false,
        statusFilter: 1,
        orderTypeFilter: 0,
        paymentTypeFilter: 0,
        invoiceNumber: null,
        recipientFilter: '',
        from: firstOfMonth,
        to: lastOfMonth,
        dirtySearch: false,
        cancelInvoiceId: null,
        cancelInvoiceNumber: '',
        cancelReason: '',
        printInvoiceNumber: '',
        printHtml: '',
        printIsReminder: false,
        printLoading: false,
    });

    function isPastDate(dateStr: string | null): boolean {
        if (!dateStr) return false;
        return new Date(dateStr) < new Date();
    }

    function formatDate(dateStr: string | null): string {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString();
    }

    function recipientDisplay(inv: Invoice): string {
        return inv.RecipientName || inv.CompanyName;
    }

    async function fetchInvoices(): Promise<void> {
        state.loading = true;
        state.dirtySearch = false;
        try {
            state.invoices = await listInvoices(
                state.invoiceNumber ? 0 : state.statusFilter,
                state.invoiceNumber ? '' : state.recipientFilter,
                state.invoiceNumber ? '' : state.from,
                state.invoiceNumber ? '' : state.to,
                state.invoiceNumber,
                state.invoiceNumber ? null : state.orderTypeFilter || null,
                state.invoiceNumber ? null : state.paymentTypeFilter || null,
            );
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load invoices' });
        } finally {
            state.loading = false;
        }
    }

    async function sendOne(inv: Invoice): Promise<void> {
        try {
            await sendInvoice(inv.InvoiceId);
            if (inv.Status === 'New') {
                state.invoices = state.invoices.filter((i) => i.InvoiceId !== inv.InvoiceId);
            }
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Invoice sent' });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to send invoice' });
        }
    }

    async function sendFiltered(): Promise<void> {
        if (!confirm(`Send ${state.invoices.length} invoices?`)) return;
        state.loading = true;
        try {
            await sendInvoices(
                state.statusFilter,
                state.recipientFilter,
                state.from,
                state.to,
                state.invoiceNumber,
                state.orderTypeFilter || null,
                state.paymentTypeFilter || null,
            );
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Invoices sent' });
            await fetchInvoices();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to send invoices' });
            state.loading = false;
        }
    }

    function openCancelModal(inv: Invoice): void {
        state.cancelInvoiceId = inv.InvoiceId;
        state.cancelInvoiceNumber = inv.InvoiceNumber;
        state.cancelReason = '';
    }

    function closeCancelModal(): void {
        state.cancelInvoiceId = null;
    }

    async function confirmCancel(): Promise<void> {
        if (!state.cancelInvoiceId) return;
        try {
            await cancelInvoice(state.cancelInvoiceId, state.cancelReason);
            state.invoices = state.invoices.filter((i) => i.InvoiceId !== state.cancelInvoiceId);
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Invoice cancelled' });
            closeCancelModal();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to cancel invoice' });
        }
    }

    async function openPrintModal(inv: Invoice, isReminder: boolean): Promise<void> {
        state.printInvoiceNumber = inv.InvoiceNumber;
        state.printIsReminder = isReminder;
        state.printHtml = '';
        state.printLoading = true;
        try {
            state.printHtml = await renderInvoice(inv.InvoiceNumber, isReminder);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to render invoice' });
            state.printInvoiceNumber = '';
        } finally {
            state.printLoading = false;
        }
    }

    function closePrintModal(): void {
        state.printInvoiceNumber = '';
        state.printHtml = '';
    }

    function printIframe(): void {
        const iframe = document.getElementById('invoice-iframe') as HTMLIFrameElement;
        iframe?.contentWindow?.print();
    }

    async function sendReminder(): Promise<void> {
        try {
            await registerReminder(state.printInvoiceNumber);
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Reminder registered' });
            closePrintModal();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to register reminder' });
        }
    }
</script>

<template>
    <div class="d-flex flex-wrap align-items-end row">
        <div class="col-md-2">
            <div class="form-group">
                <label>Status</label>
                <select
                    :value="state.statusFilter"
                    class="form-control"
                    :disabled="!!state.invoiceNumber"
                    @change="state.statusFilter = Number(($event.target as HTMLSelectElement).value); state.dirtySearch = true">
                    <option :value="1">Ready to send (new)</option>
                    <option :value="2">Unpaid</option>
                    <option :value="3">History</option>
                    <option :value="4">Paid with error</option>
                    <option :value="0">All</option>
                </select>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>Order type</label>
                <select
                    :value="state.orderTypeFilter"
                    class="form-control"
                    :disabled="!!state.invoiceNumber"
                    @change="state.orderTypeFilter = Number(($event.target as HTMLSelectElement).value); state.dirtySearch = true">
                    <option :value="0">None</option>
                    <option :value="1">ServiceSubscription</option>
                    <option :value="2">Addon</option>
                    <option :value="3">Misc</option>
                </select>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>Payment type</label>
                <select
                    :value="state.paymentTypeFilter"
                    class="form-control"
                    :disabled="!!state.invoiceNumber"
                    @change="state.paymentTypeFilter = Number(($event.target as HTMLSelectElement).value); state.dirtySearch = true">
                    <option :value="0">None</option>
                    <option :value="1">CreditCard</option>
                    <option :value="2">Invoice</option>
                    <option :value="3">PaperInvoice</option>
                    <option :value="4">EInvoice</option>
                </select>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>Invoice number</label>
                <input
                    type="number"
                    class="form-control"
                    :value="state.invoiceNumber ?? ''"
                    @input="state.invoiceNumber = ($event.target as HTMLInputElement).value ? Number(($event.target as HTMLInputElement).value) : null; state.dirtySearch = true" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>Recipient name</label>
                <input
                    type="text"
                    class="form-control"
                    placeholder="Name starts with"
                    :value="state.recipientFilter"
                    :disabled="!!state.invoiceNumber"
                    @input="state.recipientFilter = ($event.target as HTMLInputElement).value; state.dirtySearch = true" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>From</label>
                <input
                    type="date"
                    class="form-control"
                    :value="state.from"
                    :disabled="!!state.invoiceNumber"
                    @change="state.from = ($event.target as HTMLInputElement).value; state.dirtySearch = true" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>To</label>
                <input
                    type="date"
                    class="form-control"
                    :value="state.to"
                    :disabled="!!state.invoiceNumber"
                    @change="state.to = ($event.target as HTMLInputElement).value; state.dirtySearch = true" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <button type="button" class="btn btn-primary" :disabled="state.loading" @click="fetchInvoices">List</button>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <button
                    type="button"
                    class="btn btn-warning"
                    :disabled="state.dirtySearch || state.loading || state.invoices.length === 0"
                    :title="state.dirtySearch ? 'List invoices again to enable' : ''"
                    @click="sendFiltered">
                    Send filtered invoices
                </button>
            </div>
        </div>
    </div>

    <div v-if="state.loading">Loading...</div>

    <div v-if="state.invoices.length > 0" class="table-responsive">
        <table class="table table-hover table-condensed">
            <thead class="small">
                <tr>
                    <th>Invoice no</th>
                    <th>Recipient</th>
                    <th>Email</th>
                    <th>Due date</th>
                    <th class="text-right">Amount</th>
                    <th>Order types</th>
                    <th>Status</th>
                    <th>Method</th>
                    <th>Created</th>
                    <th>Created by</th>
                    <th>Reminders</th>
                    <th>Paid date</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="inv in state.invoices" :key="inv.InvoiceId">
                    <td>{{ inv.InvoiceNumber }}</td>
                    <td>{{ recipientDisplay(inv) }}</td>
                    <td>{{ inv.RecipientEmail || 'Not specified' }}</td>
                    <td :style="{ color: isPastDate(inv.DueDate) ? 'red' : '' }">{{ formatDate(inv.DueDate) }}</td>
                    <td class="text-right">
                        {{ (inv.Amount + inv.Vat).toFixed(2) }} {{ inv.CurrencyCode }}
                        <span v-if="inv.AmountPaid > 0"> (Paid: {{ inv.AmountPaid }})</span>
                    </td>
                    <td>{{ inv.OrderTypes?.join(', ') }}</td>
                    <td>{{ inv.Status }}</td>
                    <td>{{ inv.PaymentMethod }}</td>
                    <td>{{ formatDate(inv.Created) }}</td>
                    <td>{{ inv.CreatedByUserName }}</td>
                    <td>
                        <span v-if="inv.RemindersSent > 0">
                            {{ inv.RemindersSent }} sent, last {{ formatDate(inv.LastReminderSent) }}
                        </span>
                    </td>
                    <td>{{ formatDate(inv.PaidDate) }}</td>
                    <td>
                        <div class="btn-group">
                            <button type="button" class="btn btn-sm btn-default dropdown-toggle" data-toggle="dropdown">
                                <span class="glyphicon glyphicon-option-vertical"></span>
                            </button>
                            <ul class="dropdown-menu dropdown-menu-right">
                                <li v-if="inv.Status === 'New'">
                                    <a href="#" @click.prevent="sendOne(inv)">Send invoice</a>
                                </li>
                                <li v-if="inv.Status !== 'New' && inv.Status !== 'Cancelled'">
                                    <a href="#" @click.prevent="sendOne(inv)">Resend invoice</a>
                                </li>
                                <li v-if="inv.Status !== 'New' && inv.Status !== 'Cancelled'">
                                    <a href="#" @click.prevent="openPrintModal(inv, true)">Send reminder</a>
                                </li>
                                <li v-if="inv.Status !== 'Paid' && inv.Status !== 'Cancelled'">
                                    <a href="#" @click.prevent="openCancelModal(inv)">Cancel invoice</a>
                                </li>
                                <li>
                                    <a href="#" @click.prevent="openPrintModal(inv, false)">Print invoice</a>
                                </li>
                                <li role="separator" class="divider"></li>
                                <li>
                                    <a href="#" @click.prevent="downloadInvoicePdf(inv.InvoiceId)">Download PDF</a>
                                </li>
                            </ul>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <!-- Cancel invoice modal -->
    <Teleport to="body">
        <div v-if="state.cancelInvoiceId" class="modal fade in" style="display:block; background:rgba(0,0,0,0.5)">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <button type="button" class="close" @click="closeCancelModal">&times;</button>
                        <h4 class="modal-title">Cancel invoice {{ state.cancelInvoiceNumber }}</h4>
                    </div>
                    <div class="modal-body">
                        <div class="form-group">
                            <label>Reason</label>
                            <textarea v-model="state.cancelReason" class="form-control" rows="4"></textarea>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-default" @click="closeCancelModal">Close</button>
                        <button type="button" class="btn btn-danger" @click="confirmCancel">Cancel invoice</button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>

    <!-- Print / reminder modal -->
    <Teleport to="body">
        <div v-if="state.printInvoiceNumber" class="modal fade in" style="display:block; background:rgba(0,0,0,0.5)">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <button type="button" class="close" @click="closePrintModal">&times;</button>
                        <h4 class="modal-title">
                            {{ state.printIsReminder ? 'Reminder' : 'Print' }} — invoice {{ state.printInvoiceNumber }}
                        </h4>
                    </div>
                    <div class="modal-body">
                        <div v-if="state.printLoading">Loading...</div>
                        <iframe
                            v-else
                            id="invoice-iframe"
                            :srcdoc="state.printHtml"
                            style="width:100%; height:600px; border:none"></iframe>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-default" @click="closePrintModal">Close</button>
                        <button v-if="state.printIsReminder" type="button" class="btn btn-warning" @click="sendReminder">
                            Register reminder
                        </button>
                        <button type="button" class="btn btn-primary" @click="printIframe">Print</button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts">
    export default { name: 'BillingInvoices' };
</script>

<style></style>
